import {
  useMutation,
  useQuery,
  type UseMutationOptions,
  type UseQueryOptions,
} from '@tanstack/react-query';
import type {
  AcceptInviteRequest,
  AuthTokens,
  ChangePasswordRequest,
  ClientRelease,
  InviteInfo,
  LoginRequest,
  LoginResponse,
  MfaVerifyRequest,
  UserProfile,
} from '@mcgames/contracts';
import { useTranslation } from 'react-i18next';
import {
  clearSession,
  getRefreshToken,
  getSessionState,
  setSession,
  setTokens,
  setUser,
} from './auth-store.ts';
import {
  HttpError,
  NetworkError,
  http,
  isDefinitiveRejection,
  refreshSettled,
  refreshTokens,
  withRefreshLock,
} from './http.ts';

type QueryOptions<T> = Omit<UseQueryOptions<T, Error>, 'queryKey' | 'queryFn'>;

/** GET `path` as a query; the path is the query key. */
export function useApiQuery<T>(path: string, options?: QueryOptions<T>) {
  return useQuery<T, Error>({
    ...options,
    queryKey: [path],
    queryFn: () => http<T>('GET', path),
  });
}

/** A JSON mutation against `path`; the variables are the request body. */
export function useApiMutation<TBody, TResult = void>(
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  path: string,
  options?: Omit<UseMutationOptions<TResult, Error, TBody>, 'mutationFn'>,
) {
  return useMutation<TResult, Error, TBody>({
    ...options,
    mutationFn: (body) => http<TResult>(method, path, body),
  });
}

/** Whether `error` means "the server has nothing at this URL". */
export function isNotFound(error: unknown): boolean {
  return error instanceof HttpError && error.status === 404;
}

/** Turns any thrown error into a translated message; unknown codes fall back to the generic text. */
export function useErrorText(): (error: unknown) => string {
  const { t, i18n } = useTranslation('admin');
  return (error) => {
    if (error instanceof NetworkError) return t('errors.network', { ns: 'common' });
    if (error instanceof HttpError) {
      // The indexer's own limit has a text of its own; any other 429 is the rate limiter.
      if (error.status === 429 && error.code !== 'INDEXER_LIMIT_REACHED')
        return t('errors.tooManyRequests');
      // Admin texts are worded for the admin's context, so they win over the shared ones.
      for (const key of [`admin:errors.${error.code}`, `common:errors.${error.code}`]) {
        if (i18n.exists(key)) return t(key);
      }
    }
    return t('errors.generic', { ns: 'common' });
  };
}

/**
 * Restores the session of a previous visit from the stored refresh token. Anonymous afterwards when
 * there is none or it no longer works.
 */
export async function restoreSession(): Promise<void> {
  if (getRefreshToken() === null) {
    clearSession();
    return;
  }
  try {
    await refreshTokens();
    setUser(await http<UserProfile>('GET', '/api/me'));
  } catch (error) {
    // Only a definitive rejection ends the session for good. After a network failure or a 5xx the
    // stored token may still be valid, so a reload can restore the session once the server is back.
    clearSession({ keepRefreshToken: !isDefinitiveRejection(error) });
  }
}

export function useLatestRelease() {
  return useApiQuery<ClientRelease>('/api/client/releases/latest');
}

export function useInviteInfo(token: string) {
  return useApiQuery<InviteInfo>(`/api/invites/${encodeURIComponent(token)}`);
}

/** Accepts an invite. The new user's session is deliberately not kept. */
export function useAcceptInvite(token: string) {
  return useMutation<void, Error, AcceptInviteRequest>({
    mutationFn: async (body) => {
      const response = await http<LoginResponse>(
        'POST',
        `/api/invites/${encodeURIComponent(token)}/accept`,
        body,
      );
      if (response.status === 'ok') {
        // Best effort: nothing in the web UI needs this session, so revoke it right away.
        await http('POST', '/api/auth/logout', {
          refreshToken: response.tokens.refreshToken,
        }).catch(() => undefined);
      }
    },
  });
}

function keepSessionIfOk(response: LoginResponse): LoginResponse {
  if (response.status === 'ok') setSession(response.tokens, response.user);
  return response;
}

export function useLogin() {
  return useMutation<LoginResponse, Error, LoginRequest>({
    mutationFn: async (body) =>
      keepSessionIfOk(await http<LoginResponse>('POST', '/api/auth/login', body)),
  });
}

export function useVerifyMfa() {
  return useMutation<LoginResponse, Error, MfaVerifyRequest>({
    mutationFn: async (body) =>
      keepSessionIfOk(await http<LoginResponse>('POST', '/api/auth/mfa', body)),
  });
}

/**
 * Changes the own password. The server signs every session out and answers with a fresh token
 * pair, which replaces the current one so this tab (and others of this browser) stay signed in.
 */
export function useChangePassword() {
  return useMutation<void, Error, ChangePasswordRequest>({
    mutationFn: async (body) => {
      const user = getSessionState().user;
      const tokens = await http<AuthTokens>('POST', '/api/me/password', body);
      // A refresh that started before the change would store tokens the server has just revoked,
      // or end the session when the server rejects its token. Let it finish, then store the new
      // pair while no tab can refresh.
      await refreshSettled();
      await withRefreshLock(async () => setTokens(tokens));
      if (user !== null && getSessionState().status !== 'authenticated') setUser(user);
    },
  });
}

/** Revokes the refresh token on the server (best effort) and ends the local session. */
export function useLogout() {
  return useMutation<void, Error, void>({
    mutationFn: async () => {
      const refreshToken = getRefreshToken();
      try {
        if (refreshToken !== null) await http('POST', '/api/auth/logout', { refreshToken });
      } catch {
        // The token expires on its own; the local session ends either way.
      }
      clearSession();
    },
  });
}
